import React, { FC, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';
import OWImportButton from '../../ow_import_grid/OWImportButton';
import OWPoll from '../../common/OWPoll';
import {
	bulkMarkInvoicesPaid,
	getBulkMarkInvoicesPaidStatus,
} from '../../../thunks/invoices_thunks';
import { omitEmptyKeys } from '../../../utils/DataAccessUtils';
import { CSV_EXPORT_DEFAULTS } from '../../../utils/DataConstants';
import { withUserType } from '../../withUserType';

const FIELDS = [
	{
		key: 'workOrderId',
		label: 'Work Order ID',
		type: 'number',
		constraints: [{ type: 'unique' }],
	},
	{
		key: 'invoiceId',
		label: 'Invoice ID',
		type: 'number',
		constraints: [{ type: 'unique' }],
	},
];

const validateRecordsOnSubmit = (data) => {
	if (!data || data.length === 0) {
		return 'Please add at least one row.';
	}
	return null;
};

const toNumberOrUndefined = (value: any): number | undefined => {
	if (value === '' || value === null || value === undefined) return undefined;
	const num = Number(value);
	return Number.isFinite(num) ? num : undefined;
};

interface BulkMarkPaidBackendError {
	invoiceId?: number;
	workOrderId?: number;
	errorMessage: string;
}

interface BulkMarkInvoicesPaidButtonProps {
	applyFilters: (filter: { statuses: string }) => void;
	bulkMarkPaid: (records: any[]) => Promise<any>;
	getStatus: () => Promise<any>;
}

const BulkMarkInvoicesPaidButton: FC<BulkMarkInvoicesPaidButtonProps> = ({
	applyFilters,
	bulkMarkPaid,
	getStatus,
}) => {
	const [submitInitiated, setSubmitInitiated] = useState(false);
	// Holds backend per-row errors from the most recent submit. The recordHook
	// reads this via ref so the lookup is always against the freshest data
	// without invalidating the hook's identity.
	const backendErrorsRef = useRef<BulkMarkPaidBackendError[]>([]);

	const recordHook = useCallback((record: any) => {
		const workOrderId = record.get('workOrderId');
		const invoiceId = record.get('invoiceId');

		if (!workOrderId && !invoiceId) {
			record.addError('workOrderId', 'Work Order ID or Invoice ID is required');
			record.addError('invoiceId', 'Work Order ID or Invoice ID is required');
		}

		const rowInvoiceId = toNumberOrUndefined(invoiceId);
		const rowWorkOrderId = toNumberOrUndefined(workOrderId);
		backendErrorsRef.current.forEach((backendError) => {
			const matchByInvoice = rowInvoiceId !== undefined && rowInvoiceId === backendError.invoiceId;
			const matchByWorkOrder =
				rowWorkOrderId !== undefined && rowWorkOrderId === backendError.workOrderId;
			if (matchByInvoice || matchByWorkOrder) {
				const fieldKey = matchByInvoice ? 'invoiceId' : 'workOrderId';
				record.addError(fieldKey, backendError.errorMessage);
			}
		});

		return record;
	}, []);

	const onDataLoad = useCallback(
		(results) => {
			// Clear stale backend errors before each submit; the catch handler
			// repopulates them if the BE returns per-row failures.
			backendErrorsRef.current = [];

			const records = results.validData.map((row) =>
				omitEmptyKeys({
					workOrderId: toNumberOrUndefined(row.workOrderId),
					invoiceId: toNumberOrUndefined(row.invoiceId),
				})
			);
			return new Promise((resolve, reject) => {
				bulkMarkPaid(records)
					.then(() => {
						setSubmitInitiated(true);
						resolve('Bulk mark as paid initiated successfully!');
					})
					.catch((err) => {
						if (Array.isArray(err)) {
							backendErrorsRef.current = err;
							reject(
								'Some invoices could not be marked as paid. Fix the highlighted rows and try again.'
							);
						} else {
							reject(err || 'Unable to mark invoices as paid. Please contact support.');
						}
					});
			});
		},
		[bulkMarkPaid]
	);

	const showPaidInvoices = useCallback(() => {
		applyFilters({ statuses: 'Paid' });
	}, [applyFilters]);

	const handleOpenPortal = useCallback((openPortal: () => void) => {
		// Drop any backend errors from a prior session before reopening so a
		// new CSV that happens to share IDs doesn't inherit stale messages.
		backendErrorsRef.current = [];
		openPortal();
	}, []);

	const handleDownloadSample = useCallback(() => {
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'bulk_mark_invoices_paid_sample',
			headers: ['Work Order ID', 'Invoice ID'],
		});
		csvExporter.generateCsv([
			{ workOrderId: 1001, invoiceId: '' },
			{ workOrderId: '', invoiceId: 5001 },
		]);
	}, []);

	return (
		<OWPoll
			id="bulk-mark-invoices-paid"
			fetchStatusPromises={[getStatus]}
			message="Marking invoices as paid in progress!"
			successMessage="Marking invoices as paid completed!"
			successButtonText="Show invoices"
			onRefresh={showPaidInvoices}
			successMessageMandatory={submitInitiated}
			renderElementWithProps={({ disabled }) => (
				<OWImportButton
					type="invoiceIds"
					fields={FIELDS}
					onDataLoad={onDataLoad}
					recordHook={recordHook}
					validateRecordsOnSubmit={validateRecordsOnSubmit}
					onDownloadSample={handleDownloadSample}
					sampleModalTitle="Bulk Mark Invoices Paid"
					sampleModalText="Do you want to download a sample file? Provide a Work Order ID or Invoice ID per row."
					renderElement={(openPortal) => (
						<Button
							size="large"
							icon={<CloudUploadOutlined translate="" />}
							onClick={() => handleOpenPortal(openPortal)}
							disabled={disabled}
							title="Bulk Mark Paid"
						>
							<span className="inline-block-visible-xxl">Bulk Mark Paid</span>
						</Button>
					)}
				/>
			)}
		/>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	bulkMarkPaid: (records) => dispatch(bulkMarkInvoicesPaid(ownProps.userType)(records)),
	getStatus: () => dispatch(getBulkMarkInvoicesPaidStatus(ownProps.userType)()),
});

export default withUserType(connect(null, mapDispatchToProps)(BulkMarkInvoicesPaidButton));
